import { getIsoWeekNumber, getMonthDaysRaw, getWeekDays } from "../lib/DateUtils.ts";
import * as amplify from "../util/amplify.ts";
import * as i18n from "../i18n.ts";
import Icons from "../svg/Icons.ts";
import { getComputedColorVariable } from "../lib/Css.ts";


export class MonthDatePicker {
    private root: HTMLElement;
    private currentDate: Date;
    private currentShowYear :boolean = true;
    private selectedDate: Date | null = null;
    private monthNames = getMonthDaysRaw();
    private weekDays = getWeekDays();
    private showWeek :boolean;
    private showYear ?:boolean;
    private onpickdate: (date:Date,showYear:boolean)=>any;
        
    constructor(root: HTMLElement, options = {showWeek:true,showYear:undefined,onpickdate:undefined,date:undefined,firstDayOfWeek:undefined}) {
        this.root = root;
        this.showWeek = options.showWeek;
        this.showYear = options.showYear;
        this.currentDate = this.selectedDate = options.date||new Date();
        
        this.onpickdate = options.onpickdate || ((selectedDate:Date,currentShowYear:boolean)=>undefined);
        
        //reorders days according to the given parameter
        if(options.firstDayOfWeek!==undefined){
          var firstDayOfWeekIndex = this.weekDays.indexOf(this.weekDays.find(dayData=>dayData.index===options.firstDayOfWeek))
          this.weekDays = this.weekDays.slice(firstDayOfWeekIndex).concat(this.weekDays.slice(0,firstDayOfWeekIndex))
        }
        this.render();
        
        amplify.subscribe("calendar.currentDate",(selectedDate : Date)=>{this.setDate(selectedDate)})
    }
    public setDate(date: Date) {
      this.selectedDate = this.currentDate = date;
      this.updateCalendarDays();
    }
    private render() {
        this.root.classList.toggle('show-week',this.showWeek)
        this.root.innerHTML = ""; // Réinitialiser le contenu
        this.root.appendChild(this.createCalendarHeader());
        this.root.appendChild(this.createDaysGrid());
        if(this.showYear!==undefined){
          this.currentShowYear = this.showYear;
          this.root.appendChild(this.createShowYear());
        }
        this.updateCalendarDays();
    }

    private createCalendarHeader(): HTMLElement {
        const header = document.createElement("div");
        header.className = "calendar-header";

        // Titre du mois
        const title = document.createElement("span");
        title.className = "month-title";
        header.appendChild(title);

        for(var navButtonInfo of [
          {icons:Icons.ic_chevron_left_double,delta:-12,class:"navyear"},
          {icons:Icons.ic_chevron_right_double,delta:12,class:"navyear"},
          {icons:Icons.ic_chevron_left,delta:-1},
          {icons:Icons.ic_chevron_right,delta:1},
          
        ]){
          const 
            delta = navButtonInfo.delta,
            button = document.createElement("ib");
          button.className = navButtonInfo.class;  
          button.innerHTML = navButtonInfo.icons.toString();
          button.addEventListener('click',(event) => {
            event.stopImmediatePropagation()
            this.navigateMonth(delta);
          })
          header.appendChild(button);
        }
        
        return header;
    }

    private createDaysGrid(): HTMLElement {
        const daysGrid = document.createElement("div");
        daysGrid.className = "days-grid";
        daysGrid.onclick = (e: MouseEvent) => this.handleDayClick(e);
        return daysGrid;
    }
    private createShowYear(): HTMLElement {
        const bottomDiv = document.createElement("flexrow");
        bottomDiv.style.marginTop = '16px'
        bottomDiv.style.gap = '8px'
        bottomDiv.style.lineHeight = '32px'
        
        const label = document.createElement("flexauto");
        label.innerText= i18n.get("actions.showyear")
        
        const box = document.createElement("span");
        box.classList.add('onoff2')
        box.classList.toggle('on',this.showYear)
        
        const clear = document.createElement("span");
        clear.innerText= i18n.get("actions.clear")
        
        
        bottomDiv.appendChild(box)
        bottomDiv.appendChild(label)
        bottomDiv.appendChild(clear)

        box.addEventListener('click',(event: MouseEvent) => {
          event.stopPropagation();
          box.classList.toggle('on')
          const isShowYear = box.classList.contains('on')
          this.currentShowYear = isShowYear;
          this.updateCalendarDays()
        })

        clear.addEventListener('click',(event: MouseEvent) => {
          event.stopPropagation();
          const isShowYear = box.classList.contains('on')
          this.onpickdate(undefined, isShowYear)
        })

        return bottomDiv;
    }

    private updateCalendarDays() {
        const monthTitle = this.root.querySelector(".month-title");
        const daysGrid = this.root.querySelector(".days-grid");
        this.root.querySelectorAll<HTMLElement>('.navyear').forEach(it=>{
          it.style.display = this.currentShowYear?'inline-block':'none'
        })
        // Afficher le mois actuel
        const monthName = this.monthNames[this.currentDate.getMonth()]
        monthTitle.textContent = `${monthName.substring(0,1).toUpperCase()+monthName.substring(1)} ${this.currentShowYear?this.currentDate.getFullYear():''}`;

        // Calcul des jours
        daysGrid.innerHTML = "";
        const firstDayOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), 1);
        const lastDayOfMonth = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 0);
        const startDay = (firstDayOfMonth.getDay()-this.weekDays[0].index + 6)%7;
        const totalDays = lastDayOfMonth.getDate();

        const weekNumberEmpty = document.createElement("div");
        weekNumberEmpty.className = "week-number empty"
        daysGrid.appendChild(weekNumberEmpty);

        if(this.currentShowYear){
          for (const day of this.weekDays) {
              const dayElement = document.createElement("div");
              dayElement.className = "day-of-week";
              dayElement.textContent = day.letter;
              daysGrid.appendChild(dayElement);
          }
        }
        const 
            endDay = totalDays+(7-(totalDays+startDay +1)%7)%7;
        for (let day = -startDay,subCounter = 0; day <= endDay; day++) {
            const date = new Date(this.currentDate.getFullYear(), this.currentDate.getMonth(), day)
            if(subCounter % 7 === 0){
              const weekNumberCell = document.createElement("div");
              weekNumberCell.className = "week-number"
              weekNumberCell.textContent = getIsoWeekNumber(date).toString();
              daysGrid.appendChild(weekNumberCell);
            }
            subCounter++;

            const dayCell = document.createElement("div");
            dayCell.className = date.getMonth()===this.currentDate.getMonth()?"day":"day empty";
            dayCell.textContent = date.getDate().toString();
            dayCell.dataset.date = date.toISOString();
            
            // Ajouter un style spécifique pour la date courante
            if (this.isSameDay(new Date(),date)) {
                dayCell.classList.add("today");
            }
            // Ajouter un style spécifique pour la date sélectionnée
            if (this.isSameDay(new Date(this.selectedDate), date)) {
                dayCell.classList.add("selected");
            }
            daysGrid.appendChild(dayCell);
        }
    }

    private handleDayClick(event: MouseEvent) {
        const target = event.target as HTMLElement;
        if (target.classList.contains("day")) {
            this.selectedDate = new Date(target.dataset.date);
            this.sendDateSelectedEvent();
            this.updateCalendarDays();
        }
    }

    private navigateMonth(delta: number) {
        this.currentDate.setMonth(this.currentDate.getMonth() + delta);
        this.updateCalendarDays();
    }

    private isSameDay(date1: Date, date2: Date): boolean {
        return (
            date1.getDate() === date2.getDate() &&
            date1.getMonth() === date2.getMonth() &&
            date1.getFullYear() === date2.getFullYear()
        );
    }

    private sendDateSelectedEvent() {
      this.onpickdate(this.selectedDate,this.currentShowYear)
    }
}

const
  fwOnSurfaceLowOpacity = getComputedColorVariable("--fwOnSurface").setOpacityPercent(20),
  style = document.createElement('style');
style.innerHTML = `
.MonthDatePicker{
  user-select:none;
  line-height:16px;
}
.MonthDatePicker:hover{
  background-color:unset
}

.MonthDatePicker .calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}
.MonthDatePicker .calendar-header>span {
  flex:1;
}
.MonthDatePicker .calendar-header>ib {
  padding:8px;
  width:40px;
  opacity:0.8;
  cursor:pointer;
}
.MonthDatePicker .month-title {
  font-weight: bold;
}

.MonthDatePicker .days-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0px;
}
.MonthDatePicker.show-week .days-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 0px;
}
.MonthDatePicker .week-number{
  display:none;
  background-color: var(--fwOutline);
}
.MonthDatePicker.show-week .week-number{
  display:unset
}

.MonthDatePicker .day,.MonthDatePicker .day-of-week,.MonthDatePicker .week-number {
  text-align: center;
  padding: 12px;
  border-radius: 4px;
}
.MonthDatePicker>.day-of-week{

}    
.MonthDatePicker .day{
  cursor: pointer;
}

.MonthDatePicker .day.today {
  background-color:${fwOnSurfaceLowOpacity};
  color:var(--fwOnSurface);
}

.MonthDatePicker .day.selected {
  background-color: var(--fwPrimary);
  color: var(--fwOnPrimary);
}

.MonthDatePicker .empty,.MonthDatePicker .day-of-week {
  background-color: transparent;
  color:var(--fwTextSecondary);
}
.MonthDatePicker .day[data-date]:hover:not(.today):not(.selected) {
  background-color: var(--bg-selectable);
}
 
.MonthDatePicker .week-number+.day+.day+.day+.day+.day+.day+.day+.week-number{
  border-start-start-radius:0;
  border-start-end-radius: 0;
}
.MonthDatePicker .week-number:has(+.day+.day+.day+.day+.day+.day+.day+.week-number){
  border-end-start-radius:0;
  border-end-end-radius: 0;
}

`
document.head.appendChild(style)
